import { ActivityIndicator, Pressable, StyleSheet, View, ViewProps } from 'react-native'
import { AccessType, fetchAllPages, useBuildings, useFetchWorkspaces, useFloors, useStore, Workspace } from 'zens-store'
import { observer } from 'mobx-react'
import { Button, Checkbox, Color, Icon, Radius, Spacing, Text } from 'zens-storybook'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { Link } from 'zens-router'

import { usePermissions } from '@dms/hook'

const styles = StyleSheet.create({
  container: {},
  content: {
    flexDirection: 'column',
    gap: Spacing.L,
  },
  text: {
    marginTop: Spacing.S,
    color: Color.metalGrey,
  },
  webhookField: {
    marginTop: Spacing.S,
  },
  webhookCard: {
    marginTop: Spacing.M,
    borderWidth: 1,
    borderColor: Color.calmGrey,
    borderRadius: Radius.M,
    padding: Spacing.M,
  },
  webhookHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: Spacing.M,
  },
  webhookHeaderLeft: {
    flex: 1,
  },
  webhookMeta: {
    color: Color.metalGrey,
    marginTop: Spacing.XXS,
  },
  webhookCount: {
    color: Color.metalGrey,
  },
  loadingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.S,
    marginTop: Spacing.M,
  },
  tree: {
    marginTop: Spacing.M,
  },
  treeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: Spacing.XS,
  },
  treeChildren: {
    marginLeft: Spacing.L,
  },
  treeChildrenFloor: {
    marginLeft: Spacing.XL,
  },
  rowContent: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    marginLeft: Spacing.S,
  },
  rowPressable: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  rowLabel: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  rowTitle: {
    flex: 1,
  },
  rowIcon: {
    marginRight: Spacing.S,
  },
  rowValue: {
    marginLeft: Spacing.M,
    color: Color.metalGrey,
  },
  emptyState: {
    marginTop: Spacing.M,
    color: Color.metalGrey,
  },
  errorText: {
    marginTop: Spacing.M,
    color: Color.darkRed,
  },
  buttons: {
    marginTop: Spacing.L,
    flexDirection: 'row',
    marginHorizontal: -Spacing.XS,
  },
  button: {
    marginHorizontal: Spacing.XS,
  },
})

interface CompanyWebhook {
  id: number
  url: string
  type: number
  workspaceIds: number[]
}

interface CompanyWebhookResponse {
  id: number
  url: string
  type: number
  workspace_ids?: number[]
}

interface WorkspaceNode {
  id: number
  name: string
}

interface FloorNode {
  id: number
  name: string
  workspaceIds: number[]
  workspaces: WorkspaceNode[]
}

interface BuildingNode {
  id: number
  name: string
  workspaceIds: number[]
  floors: FloorNode[]
}

export interface WebhookWorkspaceSelection {
  webhookId: number
  workspaceIds: number[]
}

interface Props extends ViewProps {
  companyId: number
  onSave?: (webhookSelections: WebhookWorkspaceSelection[]) => Promise<void>
  loading?: boolean
}

function normalizeWorkspaceIds(workspaceIds: number[]) {
  return Array.from(new Set(workspaceIds.map((workspaceId) => Number(workspaceId))))
    .filter((workspaceId) => Number.isFinite(workspaceId))
    .sort((left, right) => left - right)
}

function updateWorkspaceSelection(selectedWorkspaceIds: number[], workspaceIds: number[], enabled: boolean) {
  const nextSelection = new Set(selectedWorkspaceIds)

  workspaceIds.forEach((workspaceId) => {
    if (enabled) {
      nextSelection.add(workspaceId)
      return
    }

    nextSelection.delete(workspaceId)
  })

  return normalizeWorkspaceIds(Array.from(nextSelection))
}

function countSelectedWorkspaceIds(selectedWorkspaceIds: number[], workspaceIds: number[]) {
  const selectedWorkspaceIdSet = new Set(selectedWorkspaceIds)

  return workspaceIds.reduce(
    (count, workspaceId) => (selectedWorkspaceIdSet.has(workspaceId) ? count + 1 : count),
    0,
  )
}

const CompanyWebhookSettingsForm = (props: Props) => {
  const { companyId, onSave, loading, style, ...otherProps } = props
  const { api } = useStore()
  const buildings = useBuildings({ companyId })
  const floors = useFloors({ companyId })
  const fetchWorkspaces = useFetchWorkspaces()
  const { t } = useTranslation()
  const { hasPermission } = usePermissions(companyId)
  const canManageWebhooks = useMemo(
    () => hasPermission(AccessType.Update, 'settings'),
    [hasPermission],
  )
  const [webhooks, setWebhooks] = useState<CompanyWebhook[] | undefined>(canManageWebhooks ? undefined : [])
  const [webhooksError, setWebhooksError] = useState<string>()
  const [workspaces, setWorkspaces] = useState<Workspace[] | undefined>(canManageWebhooks ? undefined : [])
  const [workspacesError, setWorkspacesError] = useState<string>()
  const [selectedWorkspaceIdsByWebhook, setSelectedWorkspaceIdsByWebhook] = useState<Record<number, number[]>>({})
  const [openedBuildingKeys, setOpenedBuildingKeys] = useState<Record<string, boolean>>({})
  const [openedFloorKeys, setOpenedFloorKeys] = useState<Record<string, boolean>>({})
  const apiRef = useRef(api)
  const fetchWorkspacesRef = useRef(fetchWorkspaces)
  const tRef = useRef(t)

  apiRef.current = api
  fetchWorkspacesRef.current = fetchWorkspaces
  tRef.current = t

  useEffect(() => {
    let active = true

    if (!canManageWebhooks) {
      setWebhooks([])
      setWebhooksError(undefined)
      return () => {
        active = false
      }
    }

    setWebhooks(undefined)
    setWebhooksError(undefined)

    void (async () => {
      try {
        const response = await apiRef.current.get<CompanyWebhookResponse[]>(`company/${companyId}/webhooks`)

        if (!active) return

        setWebhooks(
          (Array.isArray(response) ? response : []).map((webhook) => ({
            id: webhook.id,
            url: webhook.url,
            type: webhook.type,
            workspaceIds: normalizeWorkspaceIds(webhook.workspace_ids || []),
          })),
        )
      } catch (error) {
        if (!active) return
        setWebhooks([])
        setWebhooksError((error as { message?: string })?.message || tRef.current('Webhook settings could not be loaded'))
      }
    })()

    return () => {
      active = false
    }
  }, [canManageWebhooks, companyId])

  useEffect(() => {
    let active = true

    if (!canManageWebhooks) {
      setWorkspaces([])
      setWorkspacesError(undefined)
      return () => {
        active = false
      }
    }

    setWorkspaces(undefined)
    setWorkspacesError(undefined)

    void (async () => {
      try {
        const data = await fetchAllPages<Workspace>(async (page) => {
          const response = await fetchWorkspacesRef.current({
            companyId,
            pagination: { page, size: 1000 },
            sort: { field: 'name', direction: 'asc' },
            filter: { isInstalled: true },
          })

          return {
            data: response.data,
            meta: response.meta || {
              page: page + 1,
              size: response.data.length,
              total: response.data.length,
            },
          }
        })

        if (!active) return
        setWorkspaces(data)
      } catch (error) {
        if (!active) return
        setWorkspaces([])
        setWorkspacesError((error as { message?: string })?.message || tRef.current('Workspaces could not be loaded'))
      }
    })()

    return () => {
      active = false
    }
  }, [canManageWebhooks, companyId])

  useEffect(() => {
    if (!canManageWebhooks) {
      setSelectedWorkspaceIdsByWebhook({})
      return
    }

    setSelectedWorkspaceIdsByWebhook(
      (webhooks || []).reduce<Record<number, number[]>>((acc, webhook) => {
        acc[webhook.id] = normalizeWorkspaceIds(webhook.workspaceIds)
        return acc
      }, {}),
    )
  }, [canManageWebhooks, webhooks])

  const workspaceTree = useMemo<BuildingNode[]>(() => {
    const buildingItems = buildings.data || []
    const floorItems = floors.data || []
    const workspaceItems = workspaces || []

    const floorsByBuilding = new Map<number, typeof floorItems>()
    floorItems.forEach((floor) => {
      const buildingFloors = floorsByBuilding.get(floor.buildingId) || []
      buildingFloors.push(floor)
      floorsByBuilding.set(floor.buildingId, buildingFloors)
    })

    const workspacesByFloor = new Map<number, Workspace[]>()
    workspaceItems.forEach((workspace) => {
      if (!workspace.floorId) return
      const floorWorkspaces = workspacesByFloor.get(workspace.floorId) || []
      floorWorkspaces.push(workspace)
      workspacesByFloor.set(workspace.floorId, floorWorkspaces)
    })

    return [...buildingItems]
      .sort((left, right) =>
        `${left.name || ''}`.localeCompare(`${right.name || ''}`, undefined, { numeric: true, sensitivity: 'base' }),
      )
      .map((building) => {
        const buildingFloors = [...(floorsByBuilding.get(building.id) || [])]
          .sort(
            (left, right) =>
              (left.number || 0) - (right.number || 0) ||
              `${left.displayName || ''}`.localeCompare(`${right.displayName || ''}`, undefined, {
                numeric: true,
                sensitivity: 'base',
              }),
          )
          .map((floor) => {
            const floorWorkspaces = [...(workspacesByFloor.get(floor.id) || [])]
              .sort((left, right) =>
                `${left.name || ''}`.localeCompare(`${right.name || ''}`, undefined, {
                  numeric: true,
                  sensitivity: 'base',
                }),
              )
              .map((workspace) => ({
                id: workspace.id,
                name: workspace.name || `${workspace.id}`,
              }))

            const workspaceIds = floorWorkspaces.map((workspace) => workspace.id)

            return {
              id: floor.id,
              name: floor.displayName || `${floor.id}`,
              workspaceIds,
              workspaces: floorWorkspaces,
            }
          })
          .filter((floor) => floor.workspaceIds.length > 0)

        return {
          id: building.id,
          name: building.name || `${building.id}`,
          workspaceIds: buildingFloors.flatMap((floor) => floor.workspaceIds),
          floors: buildingFloors,
        }
      })
      .filter((building) => building.workspaceIds.length > 0)
  }, [buildings.data, floors.data, workspaces])

  const companyWorkspaceIds = useMemo(
    () => normalizeWorkspaceIds(workspaceTree.flatMap((building) => building.workspaceIds)),
    [workspaceTree],
  )
  const companyWorkspaceIdSet = useMemo(() => new Set(companyWorkspaceIds), [companyWorkspaceIds])
  const visibleSelectedWorkspaceIdsByWebhook = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(selectedWorkspaceIdsByWebhook).map(([webhookId, workspaceIds]) => [
          Number(webhookId),
          normalizeWorkspaceIds(workspaceIds.filter((workspaceId) => companyWorkspaceIdSet.has(workspaceId))),
        ]),
      ) as Record<number, number[]>,
    [companyWorkspaceIdSet, selectedWorkspaceIdsByWebhook],
  )

  const webhookSelections = useMemo(
    () =>
      (webhooks || []).map((webhook) => ({
        webhookId: webhook.id,
        workspaceIds: visibleSelectedWorkspaceIdsByWebhook[webhook.id] || [],
      })),
    [visibleSelectedWorkspaceIdsByWebhook, webhooks],
  )

  const isWebhookLoading = webhooks === undefined || workspaces === undefined
  const canSave = !isWebhookLoading && !webhooksError && !workspacesError

  function toggleWebhookSelection(webhookId: number, workspaceIds: number[], enabled: boolean) {
    setSelectedWorkspaceIdsByWebhook((currentSelection) => ({
      ...currentSelection,
      [webhookId]: updateWorkspaceSelection(currentSelection[webhookId] || [], workspaceIds, enabled),
    }))
  }

  function toggleRow(key: string, setter: (value: (previous: Record<string, boolean>) => Record<string, boolean>) => void) {
    setter((previous) => ({
      ...previous,
      [key]: !previous[key],
    }))
  }

  function renderExpandableRow(
    title: string,
    selectedCount: number,
    totalCount: number,
    checked: boolean,
    opened: boolean,
    onToggleSelection: (value: boolean) => void,
    onToggleOpen: () => void,
    testID: string,
  ) {
    return (
      <View style={styles.treeRow}>
        <Checkbox value={checked} onValueChange={onToggleSelection} testID={testID} />
        <View style={styles.rowContent}>
          <Pressable onPress={onToggleOpen} style={styles.rowPressable}>
            <View style={styles.rowLabel}>
              <Icon name={opened ? 'down' : 'right'} size="XS" style={styles.rowIcon} />
              <Text level={5} style={styles.rowTitle}>
                {title}
              </Text>
            </View>
            <Text style={styles.rowValue}>{t('{{selected}} / {{total}}', { selected: selectedCount, total: totalCount })}</Text>
          </Pressable>
        </View>
      </View>
    )
  }

  if (!canManageWebhooks) return null
  if (webhooks !== undefined && webhooks.length === 0 && !webhooksError) return null

  return (
    <View style={[styles.container, style]} {...otherProps}>
      <View style={styles.content}>
        <View>
          <Text level={3}>
            <Trans>Webhooks for workspaces</Trans>
          </Text>
          <Text style={styles.text}>
            <Trans>
              Select the workspaces to fire a webhook upon receiving a message from their device. Messages from workspaces that are
              not selected here will skip webhook dispatch.
            </Trans>
          </Text>
          <View style={styles.webhookField}>
            {isWebhookLoading && (
              <View style={styles.loadingRow}>
                <ActivityIndicator color={Color.premiumBlack} />
                <Text>
                  <Trans>Loading webhook settings...</Trans>
                </Text>
              </View>
            )}
            {!isWebhookLoading && !!webhooksError && <Text style={styles.errorText}>{webhooksError}</Text>}
            {!isWebhookLoading && !webhooksError && !!workspacesError && <Text style={styles.errorText}>{workspacesError}</Text>}
            {!isWebhookLoading && !webhooksError && !workspacesError && (webhooks || []).length > 0 && workspaceTree.length === 0 && (
              <Text style={styles.emptyState}>
                <Trans>No workspaces are available for this company.</Trans>
              </Text>
            )}
            {!isWebhookLoading &&
              !webhooksError &&
              !workspacesError &&
              (webhooks || []).map((webhook) => {
                const selectedWorkspaceIds = visibleSelectedWorkspaceIdsByWebhook[webhook.id] || []
                const selectedCompanyWorkspaceCount = countSelectedWorkspaceIds(selectedWorkspaceIds, companyWorkspaceIds)

                return (
                  <View key={webhook.id} style={styles.webhookCard}>
                    <View style={styles.webhookHeader}>
                      <View style={styles.webhookHeaderLeft}>
                        <Text level={4}>{webhook.url}</Text>
                        <Text style={styles.webhookMeta}>{t('Webhook type {{type}}', { type: webhook.type })}</Text>
                      </View>
                      <Text style={styles.webhookCount}>{t('{{count}} selected', { count: selectedWorkspaceIds.length })}</Text>
                    </View>
                    <View style={styles.tree}>
                      <View style={styles.treeRow}>
                        <Checkbox
                          value={companyWorkspaceIds.length > 0 && selectedCompanyWorkspaceCount === companyWorkspaceIds.length}
                          onValueChange={(value) => toggleWebhookSelection(webhook.id, companyWorkspaceIds, value)}
                          testID={`CompanyWebhookForm.Webhook.${webhook.id}.SelectAll`}
                        />
                        <View style={styles.rowContent}>
                          <View style={styles.rowPressable}>
                            <View style={styles.rowLabel}>
                              <Text level={5} style={styles.rowTitle}>
                                {t('All company workspaces')}
                              </Text>
                            </View>
                            <Text style={styles.rowValue}>
                              {t('{{selected}} / {{total}}', {
                                selected: selectedCompanyWorkspaceCount,
                                total: companyWorkspaceIds.length,
                              })}
                            </Text>
                          </View>
                        </View>
                      </View>
                      {workspaceTree.map((building) => {
                        const buildingKey = `${webhook.id}-building-${building.id}`
                        const buildingSelectedCount = countSelectedWorkspaceIds(selectedWorkspaceIds, building.workspaceIds)
                        const isBuildingOpened = openedBuildingKeys[buildingKey] || false

                        return (
                          <View key={buildingKey}>
                            {renderExpandableRow(
                              building.name,
                              buildingSelectedCount,
                              building.workspaceIds.length,
                              building.workspaceIds.length > 0 && buildingSelectedCount === building.workspaceIds.length,
                              isBuildingOpened,
                              (value) => toggleWebhookSelection(webhook.id, building.workspaceIds, value),
                              () => toggleRow(buildingKey, setOpenedBuildingKeys),
                              `CompanyWebhookForm.Webhook.${webhook.id}.Building.${building.id}`,
                            )}
                            {isBuildingOpened && (
                              <View style={styles.treeChildren}>
                                {building.floors.map((floor) => {
                                  const floorKey = `${webhook.id}-floor-${floor.id}`
                                  const floorSelectedCount = countSelectedWorkspaceIds(selectedWorkspaceIds, floor.workspaceIds)
                                  const isFloorOpened = openedFloorKeys[floorKey] || false

                                  return (
                                    <View key={floorKey}>
                                      {renderExpandableRow(
                                        floor.name,
                                        floorSelectedCount,
                                        floor.workspaceIds.length,
                                        floor.workspaceIds.length > 0 && floorSelectedCount === floor.workspaceIds.length,
                                        isFloorOpened,
                                        (value) => toggleWebhookSelection(webhook.id, floor.workspaceIds, value),
                                        () => toggleRow(floorKey, setOpenedFloorKeys),
                                        `CompanyWebhookForm.Webhook.${webhook.id}.Floor.${floor.id}`,
                                      )}
                                      {isFloorOpened && (
                                        <View style={styles.treeChildrenFloor}>
                                          {floor.workspaces.map((workspace) => (
                                            <View key={`${webhook.id}-workspace-${workspace.id}`} style={styles.treeRow}>
                                              <Checkbox
                                                label={workspace.name}
                                                value={selectedWorkspaceIds.includes(workspace.id)}
                                                onValueChange={(value) =>
                                                  toggleWebhookSelection(webhook.id, [workspace.id], value)
                                                }
                                                testID={`CompanyWebhookForm.Webhook.${webhook.id}.Workspace.${workspace.id}`}
                                              />
                                            </View>
                                          ))}
                                        </View>
                                      )}
                                    </View>
                                  )
                                })}
                              </View>
                            )}
                          </View>
                        )
                      })}
                    </View>
                  </View>
                )
              })}
          </View>
        </View>
      </View>
      <View style={styles.buttons}>
        <Button
          title={t('Save')}
          size="L"
          style={styles.button}
          onPress={() => onSave?.(webhookSelections)}
          disabled={loading || !canSave}
          testID="CompanyWebhookForm.Save"
        />
        <Link href={`/companies/${companyId}/detail`} asChild>
          <Button
            title={t('Cancel')}
            light
            size="L"
            style={styles.button}
            disabled={loading}
            testID="CompanyWebhookForm.Cancel"
          />
        </Link>
      </View>
    </View>
  )
}

interface SkeletonProps extends ViewProps {}

const Skeleton = (props: SkeletonProps) => {
  const { style, ...otherProps } = props
  const { t } = useTranslation()

  return (
    <View style={[styles.container, style]} {...otherProps}>
      <View style={styles.content}>
        <Text.Skeleton level={3} />
        <Text.Skeleton style={styles.text} numberOfLines={2} />
      </View>
      <View style={styles.buttons}>
        <Button.Skeleton size="L" style={styles.button} />
        <Button title={t('Cancel')} size="L" style={styles.button} />
      </View>
    </View>
  )
}

CompanyWebhookSettingsForm.Skeleton = Skeleton

export default observer(CompanyWebhookSettingsForm)
